import _ from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Typography from '@mui/material/Typography'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import ShareIcon from '@mui/icons-material/Share'
import { IconButton, Tooltip } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import RefreshIcon from '@mui/icons-material/Refresh'

import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'
import TabPanel from '@/components/ui/tab/TabPanel'

import { OrderStatus } from '@/core/models/order.model'

import { useDeleteMeetingLinkMutation, useEnterMeetingLinkMutation, useGetMeetingLinkQuery, useLeaveMeetingLinkMutation } from '@/core/services/meeting-link'
import { useGetMeetingLinkOrdersQuery, useLazySearchOrdersQuery } from '@/core/services/order'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { getEnv } from '@/utils/helpers'

import AvailabilitiesTable from './containers/AvailabilitiesTable'
import CreateAvailabilityDialog from './containers/CreateAvailabilityDialog'
import CreateAvailabilityWizardContainer from './containers/CreateAvailabilityWizardContainer'
import AvailabilitiesEmptyState from './components/availability-wizard/AvailabilitiesEmptyState'
import MeetingLinkInfo from './containers/MeetingLinkInfo'
import MeetingLinkStaffs from './containers/MeetingLinkStaffs'
import OrdersTable from './containers/OrdersTable'
import ShareMeetingLinkDialog from '@/pages/meeting-links/components/ShareMeetingLinkDialog'
import { getApiErrorMessage } from '@/utils/errors'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

export function MeetingLinkPage() {
  const navigate = useNavigate()
  const { meetingLinkId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const isTooSmall = useIsMobile()

  const authUser = useAppSelector(selectAuthUser)

  const { data: meetingLink } = useGetMeetingLinkQuery(meetingLinkId ?? '', {
    skip: !meetingLinkId,
    pollingInterval: 30000 // Poll every 30 seconds
  })

  const { data: meetingLinkOrders, refetch: refetchOrders } = useGetMeetingLinkOrdersQuery(
    {
      meetingLinks: meetingLinkId ? [meetingLinkId] : undefined,
      status: [OrderStatus.Pending]
    },
    {
      skip: !meetingLinkId,
      pollingInterval: 15000, // Poll every 15 seconds
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true
    }
  )

  const [enterMeetingLink] = useEnterMeetingLinkMutation()

  useEffect(() => {
    const handleEnterMeetingLink = async () => {
      if (!meetingLinkId) return
      try {
        await enterMeetingLink(meetingLinkId).unwrap()
        refetchOrders()
      } catch (error) {
        // Handle error if needed
      }
    }
    handleEnterMeetingLink()
  }, [enterMeetingLink, meetingLinkId, refetchOrders])

  const [leaveMeetingLink] = useLeaveMeetingLinkMutation()

  const [deleteMeetingLink, { isLoading: isDeletingMeetingLink }] = useDeleteMeetingLinkMutation()

  const isMeetingLinkCreator = useMemo(() => meetingLink?.createdBy === authUser?._id, [authUser, meetingLink])
  const totalCount = meetingLinkOrders?.length
  const tabs = useMemo(() => {
    if (!authUser) return []
    const tabs = [
      {
        id: 'info',
        label: t('page.meetingLink.tab.info.label'),
        isShown: true,
      },
      {
        id: 'orders',
        label: !totalCount ? (
          t('page.meetingLink.tab.orders.label')
        ) : (
          <Badge
            badgeContent={totalCount}
            color="primary"
            sx={{ alignItems: 'center', gap: '8px', '& .MuiBadge-badge': { position: 'relative', transform: 'unset' } }}
          >
            {t('page.meetingLink.tab.orders.label')}
          </Badge>
        ),
        isShown: true,
      },
      { id: 'availabilities', label: t('page.meetingLink.tab.availabilities.label'), isShown: true },
      { id: 'staffs', label: t('page.meetingLink.tab.staffs.label'), isShown: isMeetingLinkCreator },
    ]
    return tabs.filter((tab) => !!tab.isShown)
  }, [authUser, isMeetingLinkCreator, totalCount, t])

  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState(false)
  const [openShareDialog, setOpenShareDialog] = useState(false)
  const [openWizard, setOpenWizard] = useState(false)
  const [tabValue, setTabValue] = useState<string>(tabs[0].id)

  const hasAvailabilities = (meetingLink?.availabilities?.length ?? 0) > 0

  const [searchParams] = useSearchParams()

  useEffect(() => {
    if (searchParams?.get('tab')) {
      setTabValue(searchParams.get('tab') as string)
    }
  }, [searchParams])

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue)
  }

  if (!meetingLink) return null

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Container maxWidth="xl" disableGutters>
          <Box sx={{ px: 3, pt: 3 }}>
            {/* Header Section */}
            <Stack spacing={3}>
              {/* Title and Actions */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 2, sm: 0 }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
              >
                {/* Title with Back Button */}
                <Stack direction="row" alignItems="center" spacing={1}>
                  <IconButton
                    onClick={() => navigate('/meeting-links')}
                    sx={{ color: 'primary.dark' }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography
                    variant="h5"
                    component="h1"
                    sx={{
                      fontWeight: 600,
                      color: 'primary.dark',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {meetingLink.name}
                  </Typography>
                </Stack>

                {/* Action Buttons */}
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    width: { xs: '100%', sm: 'auto' }
                  }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    href={`${getEnv('VITE_MEDCONNECT_PATIENT_URL')}/meeting-links/${meetingLink.slug}`}
                    target="_blank"
                    fullWidth={isTooSmall}
                  >
                    {t('page.meetingLink.see')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<ShareIcon />}
                    onClick={() => setOpenShareDialog(true)}
                    fullWidth={isTooSmall}
                  >
                    {t('page.meetingLinks.share.title')}
                  </Button>
                  {meetingLink.createdBy === authUser?._id && (
                    <Button
                      color="error"
                      onClick={() => setOpenDeleteConfirmationDialog(true)}
                      startIcon={<DeleteRoundedIcon />}
                      variant="contained"
                      fullWidth={isTooSmall}
                    >
                      {t('common:button.delete.label')}
                    </Button>
                  )}
                </Stack>
              </Stack>

              {/* Tabs */}
              <Tabs
                value={tabValue}
                onChange={handleChangeTab}
                variant={isTooSmall ? "scrollable" : "standard"}
                scrollButtons={isTooSmall ? "auto" : false}
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  '& .MuiTabs-flexContainer': {
                    gap: 2
                  }
                }}
                visibleScrollbar
              >
                {tabs.map((tab) => (
                  <Tab
                    key={tab.id}
                    label={tab.label}
                    value={tab.id}
                    sx={{
                      minHeight: 48,
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  />
                ))}
              </Tabs>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Content Section */}
      <Container
        maxWidth="xl"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 2, sm: 3 }
        }}
      >
        <TabPanel value={tabValue} index="info">
          <MeetingLinkInfo />
        </TabPanel>
        <TabPanel value={tabValue} index="orders">
          <OrdersTable orders={meetingLinkOrders} totalCount={totalCount} onRefresh={refetchOrders} />
        </TabPanel>
        <TabPanel value={tabValue} index="availabilities">
          {!hasAvailabilities && isMeetingLinkCreator ? (
            <AvailabilitiesEmptyState onSetup={() => setOpenWizard(true)} />
          ) : (
            <Stack spacing={2}>
              {isMeetingLinkCreator && (
                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={() => setOpenWizard(true)}
                    data-testid="add-availability-button"
                  >
                    {t('page.meetingLink.wizard.addAnother', '+ Add Availability')}
                  </Button>
                </Stack>
              )}
              <AvailabilitiesTable />
            </Stack>
          )}
        </TabPanel>
        <TabPanel value={tabValue} index="staffs">
          <MeetingLinkStaffs />
        </TabPanel>
      </Container>

      <DeleteConfirmationDialog
        isDeleting={isDeletingMeetingLink}
        maxWidth={800}
        onClose={() => setOpenDeleteConfirmationDialog(false)}
        onDelete={async () => {
          if (!meetingLinkId) return
          try {
            await deleteMeetingLink(meetingLinkId).unwrap()
            setOpenDeleteConfirmationDialog(false)
            navigate('/meeting-links')
          } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.meetingLink.delete.error')), { variant: 'error' })
          }
        }}
        open={openDeleteConfirmationDialog}
        title={t('dialog.deleteConfirmation.meetingLink.title')}
        message={t('dialog.deleteConfirmation.meetingLink.message')}
      />

      <ShareMeetingLinkDialog
        open={openShareDialog}
        onClose={() => setOpenShareDialog(false)}
        meetingLink={{ name: meetingLink.name, slug: meetingLink.slug }}
      />

      <CreateAvailabilityWizardContainer
        open={openWizard}
        onClose={() => setOpenWizard(false)}
      />
    </Box>
  )
}

export default MeetingLinkPage
